import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, ArrowLeft, Calendar, MapPin, Clock } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    start_time: '',
    end_time: '',
    location: '',
    category: 'Community Events'
  });

  const categories = ['Community Events', 'Volunteer', 'Fundraising', 'Workshop', 'Other'];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchEvents();
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events?active_only=false`);
      setEvents(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch events",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const submitData = {
        ...formData,
        event_date: new Date(formData.event_date).toISOString()
      };

      if (editingEvent) {
        await axios.put(
          `${BACKEND_URL}/api/events/${editingEvent.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Success", description: "Event updated successfully" });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/events`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Success", description: "Event created successfully" });
      }
      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save event",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    const eventDate = new Date(event.event_date);
    setFormData({
      title: event.title,
      description: event.description,
      event_date: eventDate.toISOString().split('T')[0],
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      category: event.category
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/events/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Success", description: "Event deleted successfully" });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      start_time: '',
      end_time: '',
      location: '',
      category: 'Community Events'
    });
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingEvent(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) >= new Date();
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {showForm ? (
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-events-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Events List
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {showForm ? (editingEvent ? `Edit: ${editingEvent.title}` : 'Add New Event') : 'Manage Events'}
            </h1>
          </div>
          {!showForm && (
            <Button
              onClick={() => {
                setShowForm(true);
                setEditingEvent(null);
                resetForm();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-[#007A7A] hover:bg-[#007A7A]/90"
              data-testid="add-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="mb-8" data-testid="event-form">
            <CardHeader>
              <CardTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="event-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="event-category-select"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="event-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <input
                      type="text"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      placeholder="e.g., 5:00 PM"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="event-start-time-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <input
                      type="text"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      placeholder="e.g., 8:00 PM"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="event-end-time-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Gateway Community Center"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="event-location-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    placeholder="Event details..."
                    data-testid="event-description-input"
                  />
                </div>

                <div className="flex space-x-4">
                  <Button type="submit" className="bg-[#007A7A] hover:bg-[#007A7A]/90" data-testid="save-event-btn">
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-event-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!showForm && (
          <div className="space-y-4" data-testid="events-list">
          {events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No events scheduled. Click &quot;Add Event&quot; to create your first event.
            </div>
          ) : (
            events.map((event) => (
              <Card 
                key={event.id} 
                className={`hover:shadow-lg transition-shadow duration-300 ${
                  isUpcoming(event.event_date) ? 'border-l-4 border-l-[#007A7A]' : 'border-l-4 border-l-gray-300'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isUpcoming(event.event_date)
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isUpcoming(event.event_date) ? 'Upcoming' : 'Past'}
                        </span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {event.category}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(event.event_date)}
                        </span>
                        {(event.start_time || event.end_time) && (
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {event.start_time}{event.end_time && ` - ${event.end_time}`}
                          </span>
                        )}
                        {event.location && (
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {event.location}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">{event.description}</p>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => handleEdit(event)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(event.id)}
                        className="border-red-600 text-red-600 hover:bg-red-50"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminEvents;
